/**
 * @lines98/client/lib/anchored-popover — ОБЩИЙ ствол «анкер-попап у элемента-триггера».
 *
 * ЗАЧЕМ / WHY THIS EXISTS (тикет fe-achievement-detail-popover.md R-1 path A; owner-
 * directive 2026-05-30 «ствол → ветки» + 2026-06-17 reuse-audit): маленькое окошко,
 * прикреплённое ВПЛОТНУЮ к нажатому элементу (значок ачивки / `?`-кнопка), с хвостиком-
 * указателем к нему, авто-флипом над/под по измерению места, тумблером по тому же
 * триггеру и зажимом в вьюпорт. Раньше эта анкер-механика жила ТОЛЬКО внутри
 * `components/info-popover` (`positionNearTrigger`) и не переиспользовалась — деталь
 * ачивки была отдельной центрированной модалкой. Этот модуль — ЕДИНЫЙ ствол: и
 * `info-popover`, и деталь ачивки (`components/achievement-detail`) монтируются на нём,
 * без копипаста позиционирования.
 *
 * Механика top-layer / backdrop / Esc / focus-trap — из коробки native `<dialog>`
 * (`showModal()`), центрирование UA перебиваем `position:fixed` + координаты от
 * `trigger.getBoundingClientRect()` (как прежний `positionNearTrigger`).
 *
 * SCOPE: каркас попапа (dialog + header-слот + body-слот + tail), позиционирование
 *   (флип над/под, зажим в вьюпорт с `--m`), тумблер по тому же триггеру / перенос к
 *   другому, one-open-at-a-time registry, открытие/закрытие + анимация + reduce-motion,
 *   focus-return на триггер, aria-haspopup/aria-expanded на триггере. NOT IN SCOPE:
 *   содержимое (его наполняет потребитель в `body`/`title`), вид значка-триггера,
 *   данные каталога. Размеры/анимация/tail-вид — co-located `anchored-popover.css`.
 *
 * Cross-references:
 *   - tickets/fe-achievement-detail-popover.md (R-1/R-2, AC-1..AC-4, AC-10..AC-13).
 *   - components/info-popover.ts (потребитель: `?`/`!`-инфо-копи; собственный триггер).
 *   - components/achievement-detail.ts (потребитель: деталь ачивки; внешние триггеры).
 *   - components/icon-button.ts (× закрыть — общий примитив).
 *   - .claude/skills/responsive-ui-design/SKILL.md §0.E (анкер-попап у триггера).
 */

import './anchored-popover.css';

import { createIconButton, ICON_CLOSE } from '../components/icon-button.js';
import { t } from './i18n/index.js';

// =============================================================================
// Types
// =============================================================================

export interface AnchoredPopoverOptions {
  /**
   * Доступное имя крестика-закрытия (aria-label). По умолчанию «Закрыть».
   */
  readonly closeLabel?: string;
  /**
   * Доп. класс(ы) на корневой `<dialog>` — потребитель красит/наполняет свой вариант
   * (напр. `badge-detail-dialog`), НЕ переопределяя анкер-механику. Вид значка-триггера
   * и контента — забота потребителя.
   */
  readonly dialogClassName?: string;
  /**
   * Уникальный id для `<dialog>` (используется в `aria-controls` триггера). Авто-
   * генерируется, если не задан.
   */
  readonly id?: string;
  /**
   * Показывать ли крестик-закрытия в шапке. По умолчанию `true`. (Тактильные 44px из
   * примитива icon-button.)
   */
  readonly showCloseButton?: boolean;
  /**
   * Опциональный поставщик ГРАНИЦЫ зажима попапа (owner-directive 2026-06-21): вызывается
   * при позиционировании, возвращает прямоугольник, ЗА который попап не выезжает (или null —
   * клампим к вьюпорту). Потребитель решает, что граница (напр. деталь ачивки → таблица
   * Профиля). Свежий rect берётся на КАЖДОЕ открытие (учитывает скролл/ресайз).
   */
  readonly getBoundary?: () => DOMRect | null;
}

export interface AnchoredPopoverHandle {
  /** Корневой `<dialog>` (в `document.body`). Потребитель НЕ позиционирует его сам. */
  readonly dialog: HTMLDialogElement;
  /**
   * Контейнер заголовка попапа (`<h*>` кладёт потребитель). Слева от крестика.
   */
  readonly titleSlot: HTMLElement;
  /**
   * Контейнер тела попапа — потребитель наполняет компактным контентом.
   */
  readonly body: HTMLElement;
  /**
   * Открыть/перенести попап у указанного триггера, ЛИБО закрыть, если он уже открыт у
   * ЭТОГО ЖЕ триггера (тумблер, owner-decision 3). Тап по ДРУГОМУ триггеру — переносит
   * попап к нему (старый закрывается тем же экземпляром).
   *
   * @param trigger элемент, у которого якорится попап (значок/кнопка). Получает
   *   `aria-haspopup="dialog"` + `aria-expanded`.
   */
  toggle(trigger: HTMLElement): void;
  /** Открыть/перенести попап у триггера (без тумблер-закрытия). */
  open(trigger: HTMLElement): void;
  /** Закрыть попап (если открыт). */
  close(): void;
  /** Текущий триггер, у которого открыт попап (null — закрыт). */
  currentTrigger(): HTMLElement | null;
  /**
   * Снять попап: закрыть, отвязать слушатели, удалить `<dialog>` из DOM. Триггеры —
   * собственность потребителя (он их и удаляет); тут только чистим aria на текущем.
   */
  destroy(): void;
}

/** Предпочитаемая сторона раскрытия попапа (флип на противоположную, если не влезает). */
export type PopoverPlacement = 'above' | 'below';

export interface PositionPopoverOptions {
  /**
   * Предпочитаемая сторона. `'above'` (default — owner-decision 2 для детали ачивки:
   * над триггером) или `'below'` (инфо-копи `?`-кнопки: под кнопкой). Если выбранная
   * сторона не вмещает попап целиком — флип на противоположную (по ИЗМЕРЕНИЮ места).
   */
  readonly prefer?: PopoverPlacement;
  /**
   * Опциональный элемент-хвостик внутри попапа (анкер-попап). Если передан — ему ставится
   * `left` под центр триггера и переключается класс стороны на родителе попапа.
   */
  readonly tail?: HTMLElement | null;
  /**
   * Опциональная ГРАНИЦА, за которую попап НЕ ДОЛЖЕН выезжать ПО ГОРИЗОНТАЛИ (owner-directive
   * 2026-06-21 для детали ачивки: «плашка не выезжает за размер таблицы»). Если передана:
   *   • не шире границы (max-width = min(boundary.width, вьюпорт−2m));
   *   • левый/правый край зажат в [boundary.left, boundary.right] ∩ вьюпорт (левая ачивка →
   *     растёт вправо, правая → влево, внутрь границы).
   * ВЕРТИКАЛЬ при этом ВСЕГДА по вьюпорту (НЕ по границе): на page-flow таблица уезжает за
   * верх экрана при скролле → клампить верх к boundary.top нельзя (плашка улетала вверх,
   * owner-bug 320×480 2026-06-21). Без границы — горизонталь тоже по вьюпорту (info-popover).
   */
  readonly boundary?: DOMRect | null;
}

/**
 * Поставить ЛЮБОЙ `<dialog>`-попап у элемента-триггера: `position:fixed`, левый край
 * попапа = левый край триггера (зажат в вьюпорт с `--m`); по умолчанию НАД триггером
 * (`prefer:'above'`), флип на противоположную сторону, если выбранная не вмещает попап
 * целиком. Зажим обоих краёв исключает обрезку (P1 §0.A, AC-10). ЕДИНЫЙ источник анкер-
 * позиционирования (R-1): и `createAnchoredPopover`, и `info-popover` зовут эту функцию —
 * дубля `getBoundingClientRect`-логики нет.
 *
 * @returns фактическая сторона размещения (после возможного флипа).
 */
export function positionPopoverNearTrigger(
  dialog: HTMLDialogElement,
  trigger: HTMLElement,
  opts: PositionPopoverOptions = {},
): PopoverPlacement {
  const prefer: PopoverPlacement = opts.prefer ?? 'above';
  const t = trigger.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const rootStyles = getComputedStyle(document.documentElement);
  const m = parseFloat(rootStyles.getPropertyValue('--m')) || 8;
  const gap = m * 0.6; // зазор попап↔триггер (под хвостик) — доля delta (§0.F).

  dialog.style.position = 'fixed';
  dialog.style.margin = '0';
  dialog.style.right = 'auto';
  dialog.style.bottom = 'auto';

  // ГРАНИЦА (owner-directive 2026-06-21): если задана — попап не шире границы и по ГОРИЗОНТАЛИ
  // зажат в её прямоугольник. ВАЖНО (owner-bug 2026-06-21, 320×480): граница применяется ТОЛЬКО
  // по горизонтали (left/right/width — они НЕ зависят от вертикального скролла → консистентно).
  // По ВЕРТИКАЛИ всегда вьюпорт: на page-flow таблица уезжает за верх экрана при скролле →
  // boundary.top уходил в минус → плашка улетала вверх off-screen, и результат «прыгал» от
  // прошлого скролла. Cap max-width = min(граница, вьюпорт−2m) ДО измерения d.
  const b = opts.boundary ?? null;
  dialog.style.maxWidth = b ? `${Math.round(Math.min(b.width, vw - 2 * m))}px` : '';

  // Размеры попапа — через offsetWidth/offsetHeight (БЕЗ transform!). Критично: попап
  // открывается с scale-анимацией (0.96→1, anchored-popover.css), а getBoundingClientRect()
  // возвращает РАЗМЕР С УЧЁТОМ transform → во время анимации он МЕНЬШЕ финального → клампим по
  // заниженной ширине → после анимации плашка шире и ВЫЕЗЖАЕТ за границу, причём по-разному в
  // зависимости от тайминга (owner-bug 320×480 2026-06-21: «то выезжает, то нет»). offsetWidth/
  // Height — layout-бокс, transform не влияет → стабильно и корректно.
  const dw = dialog.offsetWidth;
  const dh = dialog.offsetHeight;

  // Горизонталь: в пределах границы (таблицы) И вьюпорта. Вертикаль: всегда вьюпорт (см. выше).
  const leftMin = b ? Math.max(m, b.left) : m;
  const leftMax = (b ? Math.min(vw - m, b.right) : vw - m) - dw;
  const topMin = m;

  // --- Горизонталь: левый край попапа = левый край триггера, зажат в [leftMin, leftMax] ---
  // (левая ачивка → растёт вправо; правая → зажата к правому краю границы = растёт влево.)
  let left = t.left;
  left = Math.max(leftMin, Math.min(left, leftMax));

  // ЖЁСТКИЙ КАП ПРАВОГО КРАЯ (owner-bug 320×480 2026-06-21): после фиксации left ограничиваем
  // max-width расстоянием от left до правого предела (граница ∩ вьюпорт). Тогда правый край
  // НИКОГДА не выезжает за границу, ДАЖЕ если контент дораскладывается шире уже после замера
  // (шрифт/иконки/снятие скроллбара) — он просто переносится внутри капа, а не вылезает.
  if (b) {
    const rightCap = Math.round(Math.min(vw - m, b.right) - left);
    if (rightCap > 0) dialog.style.maxWidth = `${rightCap}px`;
  }

  // --- Вертикаль: решаем СТОРОНУ (над/под), затем ЯКОРИМ КРАЙ у значка ---
  const fitsAbove = t.top - gap - dh >= topMin;
  const fitsBelow = t.bottom + gap + dh <= vh - m;
  const moreSpaceBelow = vh - t.bottom >= t.top;
  let placedBelow: boolean;
  if (prefer === 'above') {
    placedBelow = !fitsAbove && (fitsBelow || moreSpaceBelow);
  } else {
    placedBelow = fitsBelow || (!fitsAbove && moreSpaceBelow);
  }

  dialog.style.left = `${Math.round(left)}px`;
  // ЯКОРЬ КРАЯ, ОБРАЩЁННОГО К ЗНАЧКУ (owner-bug 2026-06-21 «описание уезжает вниз значка, хвост
  // в центре»): НАД значком фиксируем НИЖНИЙ край (bottom) у значка → плашка растёт ВВЕРХ; ПОД
  // — фиксируем ВЕРХ (top) → растёт ВНИЗ. Так дораскладка высоты (шрифт/контент ПОСЛЕ замера —
  // та же причина, что у ширины) НЕ наезжает на значок, а хвостик всегда у границы значка.
  // Раньше top считался по ЗАНИЖЕННОЙ высоте (scale-анимация/settle) → плашка налезала на значок.
  if (placedBelow) {
    dialog.style.top = `${Math.round(t.bottom + gap)}px`;
    dialog.style.bottom = 'auto';
  } else {
    dialog.style.top = 'auto';
    dialog.style.bottom = `${Math.round(vh - (t.top - gap))}px`;
  }

  // --- Хвостик (если есть): сторона, обращённая к триггеру; left под центр триггера ---
  dialog.classList.toggle('anchored-popover--below', placedBelow);
  dialog.classList.toggle('anchored-popover--above', !placedBelow);
  if (opts.tail) {
    const triggerCenterX = t.left + t.width / 2;
    // Зажать, чтобы хвостик не вылез за скруглённые углы (отступ ≈ m от краёв).
    const tailX = Math.max(m, Math.min(triggerCenterX - left, dw - m));
    opts.tail.style.left = `${Math.round(tailX)}px`;
  }

  return placedBelow ? 'below' : 'above';
}

// =============================================================================
// Global registry — «один открытый попап за раз» (AC-4, как info-popover registry)
// =============================================================================

const openRegistry = new Set<() => void>();

function closeAllOpen(except?: () => void): void {
  for (const closeFn of openRegistry) {
    if (closeFn !== except) closeFn();
  }
}

// =============================================================================
// ID generator
// =============================================================================

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `anchored-popover-${idCounter}`;
}

// =============================================================================
// Mount
// =============================================================================

/**
 * Создать экземпляр анкер-попапа. Возвращает handle; потребитель наполняет `titleSlot`
 * + `body`, навешивает на свои триггеры `handle.toggle(triggerEl)`.
 */
export function createAnchoredPopover(opts: AnchoredPopoverOptions = {}): AnchoredPopoverHandle {
  const id = opts.id ?? nextId();

  const dialog = document.createElement('dialog');
  dialog.className = 'anchored-popover';
  if (opts.dialogClassName) {
    dialog.classList.add(...opts.dialogClassName.split(/\s+/).filter(Boolean));
  }
  dialog.id = id;

  // ---- Header (слот заголовка + крестик) ----
  const header = document.createElement('header');
  header.className = 'anchored-popover-header';

  const titleSlot = document.createElement('div');
  titleSlot.className = 'anchored-popover-title-slot';
  header.appendChild(titleSlot);

  let closeBtn: HTMLButtonElement | null = null;
  if (opts.showCloseButton !== false) {
    closeBtn = createIconButton({
      glyph: ICON_CLOSE,
      label: opts.closeLabel ?? t('game.infoPopover.close'),
      variant: 'ghost',
      className: 'anchored-popover-close',
    }).element;
    header.appendChild(closeBtn);
  }
  dialog.appendChild(header);

  // ---- Body (слот контента) ----
  const body = document.createElement('div');
  body.className = 'anchored-popover-body';
  const bodyId = `${id}-body`;
  body.id = bodyId;
  dialog.setAttribute('aria-describedby', bodyId);
  dialog.appendChild(body);

  // ---- Tail (хвостик-указатель к триггеру) ----
  // Позиция/сторона (сверху/снизу) ставится в positionNearTrigger по результату флипа.
  const tail = document.createElement('div');
  tail.className = 'anchored-popover-tail';
  tail.setAttribute('aria-hidden', 'true');
  dialog.appendChild(tail);

  document.body.appendChild(dialog);

  // ---- Internal state ----
  let unmounted = false;
  let activeTrigger: HTMLElement | null = null;
  // «Взведён» ли scroll-close: ставим true ПОСЛЕ кадра открытия (чтобы возможный скролл
  // момента открытия не закрыл попап мгновенно). Сбрасывается на закрытии.
  let scrollArmed = false;

  const setTriggerExpanded = (trigger: HTMLElement | null, expanded: boolean): void => {
    if (!trigger) return;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', id);
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  /**
   * Поставить попап у триггера через ЕДИНЫЙ анкер-помощник (по умолчанию НАД триггером —
   * owner-decision 2; флип вниз, если сверху мало места). Хвостик ставится автоматически
   * на сторону, обращённую к триггеру (AC-2).
   */
  function positionNearTrigger(trigger: HTMLElement): void {
    positionPopoverNearTrigger(dialog, trigger, {
      prefer: 'above',
      tail,
      boundary: opts.getBoundary?.() ?? null,
    });
  }

  const closeFn = (): void => {
    if (unmounted) return;
    if (dialog.open) {
      dialog.classList.add('anchored-popover--closing');
      window.requestAnimationFrame(() => {
        dialog.close();
        dialog.classList.remove('anchored-popover--closing');
      });
    }
    setTriggerExpanded(activeTrigger, false);
    const returnTo = activeTrigger;
    activeTrigger = null;
    scrollArmed = false;
    openRegistry.delete(closeFn);
    // Вернуть фокус на триггер (AC-16), defensive — он мог быть удалён потребителем.
    if (returnTo && returnTo.isConnected && typeof returnTo.focus === 'function') {
      returnTo.focus();
    }
  };

  const openFn = (trigger: HTMLElement): void => {
    if (unmounted) return;
    // AC-4: закрыть любой другой открытый попап (в т.ч. этот, чтобы пере-якориться).
    closeAllOpen(closeFn);
    if (dialog.open) dialog.close();

    // Снять expanded со старого триггера (перенос к другому — AC-4).
    if (activeTrigger && activeTrigger !== trigger) setTriggerExpanded(activeTrigger, false);
    activeTrigger = trigger;

    dialog.showModal();
    positionNearTrigger(trigger);
    setTriggerExpanded(trigger, true);
    openRegistry.add(closeFn);
    // ПЕРЕ-ПОЗИЦИОНИРОВАНИЕ после settle layout (owner-bug 320×480 2026-06-21: «то выезжает,
    // то нет»). На showModal браузер блокирует скролл → убирает полосу прокрутки страницы →
    // лэйаут РАЗЪЕЗЖАЕТСЯ шире (+~scrollbar), а контент (иконки награды) дораскладывается. Если
    // клампить ТОЛЬКО на первом кадре — размеры заниженные → плашка после settle выезжает за
    // границу. rAF-репозиция меряет уже устоявшиеся размеры (offsetWidth) → корректный зажим;
    // scale-анимация (150ms) маскирует микро-сдвиг. Guard: окно ещё открыто у того же триггера.
    window.requestAnimationFrame(() => {
      if (!unmounted && dialog.open && activeTrigger === trigger) positionNearTrigger(trigger);
      // Взвести scroll-close ПОСЛЕ кадра открытия (скролл момента открытия не закроет попап).
      scrollArmed = true;
    });
  };

  const toggleFn = (trigger: HTMLElement): void => {
    if (unmounted) return;
    // Тумблер: тот же триггер и попап открыт → закрыть (owner-decision 3).
    if (dialog.open && activeTrigger === trigger) {
      closeFn();
      return;
    }
    openFn(trigger);
  };

  // ---- Listeners ----
  const onCloseClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    closeFn();
  };
  if (closeBtn) closeBtn.addEventListener('click', onCloseClick);

  // Клик-вне (по ::backdrop / padding-зоне диалога) → закрыть (AC-4). target===dialog
  // истинно при попадании по backdrop.
  const onDialogClick = (e: MouseEvent): void => {
    if (e.target === dialog) closeFn();
  };
  dialog.addEventListener('click', onDialogClick);

  // Esc → native dialog шлёт `close`; синхронизируем aria/registry/focus-return.
  const onDialogClose = (): void => {
    if (unmounted) return;
    const returnTo = activeTrigger;
    setTriggerExpanded(activeTrigger, false);
    activeTrigger = null;
    scrollArmed = false;
    openRegistry.delete(closeFn);
    if (returnTo && returnTo.isConnected && typeof returnTo.focus === 'function') {
      returnTo.focus();
    }
  };
  dialog.addEventListener('close', onDialogClose);

  // СКРОЛЛ СТРАНИЦЫ → ЗАКРЫТЬ (owner-bug 2026-06-21: попап position:fixed «отрывался» от значка
  // при прокрутке — модалка-<dialog> не блокирует фон-скролл). Закрываем, чтобы попап никогда не
  // висел оторванным от значка. `scrollArmed` гасит ложное закрытие на скролле момента открытия.
  // capture+passive: ловим скролл любого контейнера (страница page-flow / вложенный скролл).
  const onPageScroll = (): void => {
    if (!unmounted && scrollArmed && dialog.open) closeFn();
  };
  window.addEventListener('scroll', onPageScroll, { capture: true, passive: true });

  return {
    dialog,
    titleSlot,
    body,
    toggle: toggleFn,
    open: openFn,
    close: closeFn,
    currentTrigger: () => activeTrigger,
    destroy(): void {
      if (unmounted) return;
      unmounted = true;
      openRegistry.delete(closeFn);
      if (closeBtn) closeBtn.removeEventListener('click', onCloseClick);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('close', onDialogClose);
      window.removeEventListener('scroll', onPageScroll, { capture: true } as EventListenerOptions);
      setTriggerExpanded(activeTrigger, false);
      activeTrigger = null;
      if (dialog.open) dialog.close();
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    },
  };
}
